import { AppstoreOutlined } from "@ant-design/icons"
import { Select, Tag } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { getProjectTypeDetails } from "../../../Templates/services/project-types.services.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "../overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../overview/OverviewCardIcon.tsx"
import {
  serializeMilestonesForPatch,
  sortMilestones
} from "./lead-scope-milestones.ts"
import {
  type SelectedProjectType,
  projectTypesNeedingTemplates,
  resolveProjectPhasesOnTypeChange
} from "./project-phases-on-type-change.ts"

interface ScopeProjectTypeValues {
  project_type: string[]
}

interface LeadScopeProjectTypeCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadScopeProjectTypeCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadScopeProjectTypeCardProps) {
  const { t } = useTranslation()

  const options = filterOptions?.project_type ?? []
  const values: ScopeProjectTypeValues = {
    project_type: lead?.project_type ?? []
  }

  // The configured list answers first so a save shows its new types at once;
  // `project_type_details` only catches up on the next read of the lead.
  const typeNames = useMemo(() => {
    const configured = new Map(
      options.map((option) => [String(option?.value), String(option?.label)])
    )
    const detailed = new Map(
      (lead?.project_type_details ?? []).map((type) => [type?.id, type?.name])
    )

    return (lead?.project_type ?? []).map(
      (id) => configured.get(id) ?? detailed.get(id) ?? id
    )
  }, [options, lead?.project_type, lead?.project_type_details])

  const loadTemplates = async (
    selectedIds: string[],
    currentPhases: LeadFormDataPhase[]
  ): Promise<SelectedProjectType[]> => {
    const wanted = new Set(
      projectTypesNeedingTemplates(selectedIds, currentPhases)
    )

    const templates = await Promise.all(
      selectedIds.map(async (id) => {
        if (!wanted.has(id)) return { value: id }

        const res = await getProjectTypeDetails(id)
        return { value: id, phases: res?.ok ? res?.data?.phases : undefined }
      })
    )

    return templates
  }

  const savePatch = async ({
    project_type
  }: Partial<ScopeProjectTypeValues>) => {
    if (!project_type) return true

    const currentPhases = sortMilestones(
      lead?.project_phases as LeadFormDataPhase[] | null
    )
    const templates = await loadTemplates(project_type, currentPhases)
    const resolved = resolveProjectPhasesOnTypeChange(
      templates,
      currentPhases,
      !lead?.project_type?.length
    )

    const project_phases = serializeMilestonesForPatch(resolved)
    const unchanged =
      JSON.stringify(project_phases) ===
      JSON.stringify(serializeMilestonesForPatch(currentPhases))

    return onPatch({
      project_type,
      ...(unchanged ? {} : { project_phases })
    })
  }

  return (
    <WorkspaceCard<ScopeProjectTypeValues>
      cardId={"scope-project-type"}
      title={
        <OverviewCardTitle icon={<AppstoreOutlined />} accent={"blue"}>
          {t("leadWorkspace.scope.projectType.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.scope.projectType.title")}
      canEdit={canEdit}
      values={values}
      onSave={savePatch}
      renderEdit={({ values, setValues, saving }) => (
        <LeadOverviewField label={t("leadWorkspace.scope.projectType.label")}>
          <Select
            autoFocus
            mode={"multiple"}
            allowClear
            className={"w-full"}
            disabled={saving}
            aria-label={t("leadWorkspace.scope.projectType.label")}
            optionFilterProp={"label"}
            options={options}
            value={values.project_type}
            onChange={(next) => setValues({ project_type: next })}
          />
        </LeadOverviewField>
      )}
    >
      <LeadOverviewField
        label={t("leadWorkspace.scope.projectType.label")}
        value={typeNames}
        placeholder={t("leadWorkspace.scope.projectType.empty")}
      >
        <span>
          {typeNames.map((name) => (
            <Tag key={name}>{name}</Tag>
          ))}
        </span>
      </LeadOverviewField>
    </WorkspaceCard>
  )
}

export { type ScopeProjectTypeValues, LeadScopeProjectTypeCard }
